import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export interface CartItem {
  productId: string;
  variantId?: string;
  variantName?: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
}

function validateCartItems(data: unknown): CartItem[] {
  if (!Array.isArray(data)) return [];
  const MAX_ITEMS = 50;
  const validated: CartItem[] = [];
  for (const item of data.slice(0, MAX_ITEMS)) {
    if (item == null || typeof item !== 'object') continue;
    const productId = item.productId;
    const name = item.name;
    const price = item.price;
    const quantity = item.quantity;
    if (typeof productId !== 'string' || productId.length === 0) continue;
    if (typeof name !== 'string' || name.length > 200) continue;
    if (typeof price !== 'number' || price < 0 || price > 999999) continue;
    if (typeof quantity !== 'number' || !Number.isInteger(quantity) || quantity < 1 || quantity > 99) continue;
    validated.push({
      productId,
      name,
      price,
      quantity,
      ...(typeof item.variantId === 'string' ? { variantId: item.variantId } : {}),
      ...(typeof item.variantName === 'string' ? { variantName: item.variantName } : {}),
      ...(typeof item.image === 'string' && (item.image.startsWith('http://') || item.image.startsWith('https://')) ? { image: item.image } : {}),
    });
  }
  return validated;
}

interface CartContextValue {
  items: CartItem[];
  total: number;
  itemCount: number;
  addItem: (item: CartItem) => void;
  removeItem: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => void;
  clearCart: () => void;
}

const CART_STORAGE_KEY = 'lipstick-platform-storefront-cart';

const CartContext = createContext<CartContextValue | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined') {
      return [];
    }

    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return validateCartItems(parsed);
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (item: CartItem) => {
    setItems((current) => {
      const index = current.findIndex(
        (entry) => entry.productId === item.productId && entry.variantId === item.variantId
      );

      if (index === -1) {
        return [...current, item];
      }

      const next = [...current];
      next[index] = {
        ...next[index],
        quantity: next[index].quantity + item.quantity,
      };
      return next;
    });
  };

  const removeItem = (productId: string, variantId?: string) => {
    setItems((current) =>
      current.filter((item) => !(item.productId === productId && item.variantId === variantId))
    );
  };

  const updateQuantity = (productId: string, quantity: number, variantId?: string) => {
    if (quantity <= 0) {
      removeItem(productId, variantId);
      return;
    }

    setItems((current) =>
      current.map((item) =>
        item.productId === productId && item.variantId === variantId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(CART_STORAGE_KEY);
    }
  };

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        total,
        itemCount,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
